import { Box } from '@gemini/core';
import { Button } from '@gemini/ui';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    LocationDotFilledIcon,
    LocationDotIcon,
} from '@gemini/icons';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';

import { useBreakpoint } from '@/frontend/src/hooks/useBreakpoint';
import {
    useAppContext,
    useAppDispatch,
} from '@/frontend/src/contexts/appContext';
import { EActionType, EActiveView } from '@/frontend/src/types/context';
import { isFrenchPortal } from '@/frontend/src/helpers/portal';
import { getCtaTargetUrl } from '@/frontend/src/helpers/ctaTargetUrl';
import { NAME_SPACE } from '@/frontend/src/hooks/useLoadTranslations';

const ClosestLocationResult = () => {
    const {
        context,
        mobileBreakpoint,
        ctaButtonInfo,
        closestLocationResult,
        portal,
        tracking,
    } = useAppContext();

    const { isMobile } = useBreakpoint(mobileBreakpoint);
    const { t } = useTranslation(NAME_SPACE);
    const { handleSubmit, control } = useFormContext();
    const dispatch = useAppDispatch();

    const {
        postalCode = '',
        city = '',
        street = '',
        houseNumber = '',
    } = useWatch({ control });

    const onClickGoBack = () => {
        dispatch({
            type: EActionType.SetActiveView,
            payload: EActiveView.ClosestLocationSearch,
        });
    };

    const getFromattedAddress = () => {
        return isFrenchPortal(portal)
            ? `${houseNumber} ${street}`.trim() + `, ${postalCode} ${city}`
            : `${street} ${houseNumber}`.trim() + `, ${postalCode} ${city}`;
    };

    const onSubmit = () => {
        if (!ctaButtonInfo.url) {
            return;
        }

        window.location.href = getCtaTargetUrl({
            context,
            ctaTargetUrl: ctaButtonInfo.url,
            locationResult: closestLocationResult,
            tracking,
            isClosestLocation: true,
        });
    };

    return (
        <Box
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            backgroundColor="color.surface.default.default"
            borderRadius="radius.16"
            display="grid"
            gridTemplateColumns={isMobile ? '1fr' : '1fr auto'}
            gap="spacing.16"
            gridTemplateAreas={
                isMobile
                    ? '"result" "estimateCTA" "search"'
                    : '"result estimateCTA"  "search ."'
            }
        >
            <Box
                gridArea="result"
                display="grid"
                gridTemplateAreas='"titleResult" "suggestedAddress"'
                gap="spacing.12"
            >
                <Box
                    gridArea="titleResult"
                    typography="typography.body.14.bold"
                >
                    {t('common.suggestedAddressTitle')}
                </Box>
                <Box
                    gridArea="suggestedAddress"
                    paddingLeft="spacing.12"
                    display="flex"
                    alignItems="center"
                >
                    <Box
                        as="span"
                        paddingRight="spacing.8"
                    >
                        <LocationDotFilledIcon color="inherit" />
                    </Box>
                    <Box as="span">{closestLocationResult.displayName}</Box>
                </Box>
            </Box>

            <Box
                gridArea="search"
                display="grid"
                gridTemplateAreas='"titleSearch" "enteredAddress" "updateCta"'
                gap="spacing.12"
            >
                <Box
                    gridArea="titleSearch"
                    typography="typography.body.14.bold"
                >
                    {t('common.enteredAddressTitle')}
                </Box>
                <Box
                    gridArea="enteredAddress"
                    paddingLeft="spacing.12"
                    display="flex"
                    alignItems="center"
                >
                    <Box
                        as="span"
                        paddingRight="spacing.8"
                    >
                        <LocationDotIcon color="inherit" />
                    </Box>
                    <Box as="span">{getFromattedAddress()}</Box>
                </Box>
                <Box
                    gridArea="updateCta"
                    display="flex"
                    width={isMobile ? '' : 'fit-content'}
                    flexDirection="column"
                    marginTop="spacing.16"
                    marginBottom="spacing.16"
                >
                    <Button
                        type="button"
                        variant="tertiary"
                        icon={ArrowLeftIcon}
                        iconPosition="start"
                        onPress={onClickGoBack}
                    >
                        {t('common.updateAddressButton.label')}
                    </Button>
                </Box>
            </Box>

            <Box
                gridArea="estimateCTA"
                display="flex"
                flexDirection="column"
            >
                <Button
                    type="submit"
                    icon={ArrowRightIcon}
                    iconPosition="end"
                    isFullWidth
                    marginTop="auto"
                >
                    {t('common.estimateAddressButton.label')}
                </Button>
            </Box>
        </Box>
    );
};

export default ClosestLocationResult;
